import { Check, Plus, X } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  SearchInput,
  SkeletonRows,
  TextField,
} from "@/components/ui";
import { useDebounced } from "@/lib/useDebounced";
import type {
  AdoptedMaster,
  MasterProduct,
} from "@/repositories/catalogueRepository";

import { ProductTile } from "./panels";

import {
  useAdoptedMasters,
  useCopyFromMaster,
  useMasterSearch,
} from "./useCatalogueData";

/**
 * Add a product from the shared list.
 *
 * The alternative this replaces is typing a name, a brand, a pack size, a
 * category and a VAT setting on a tablet, four hundred times. Here a shopkeeper
 * types "coke", sets their own price, and the rest arrives already correct —
 * including the nicknames, so their cashiers can find it by typing "coke" too.
 *
 * The price is the one field they must supply. The shared list carries an
 * indicative figure to save typing, and it is a starting point, not a
 * recommendation — what a shop charges is the shop's business.
 */
export function MasterCataloguePanel({ onClose }: { onClose: () => void }) {
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term, 250);
  const [added, setAdded] = useState<Record<string, string>>({});

  const results = useMasterSearch(debounced, "", debounced.trim().length > 0);
  // Fetched up front rather than per search, so a result is marked the moment
  // it renders. See `adoptedMasters` in the repository for why it lives on the
  // products endpoint.
  const adopted = useAdoptedMasters();
  const alreadyMine = new Map(
    (adopted.data ?? []).map((row) => [row.master_id, row]),
  );

  return (
    <Card className="space-y-4">
      <CardHeader
        title="Add from the shared list"
        subtitle="Common South African products, described once. Set your own price."
        action={
          <Button
            variant="ghost"
            onClick={onClose}
            icon={<X className="size-4" aria-hidden />}
          >
            Close
          </Button>
        }
      />

      <SearchInput
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Search — try “coke”, “mielie meal” or “washing powder”"
        autoFocus
      />

      {debounced.trim() === "" ? (
        <EmptyState
          title="What are you looking for?"
          description="Type what you or your customers call it. Nicknames work."
        />
      ) : results.isLoading ? (
        <SkeletonRows rows={4} />
      ) : results.data && results.data.length > 0 ? (
        <ul className="divide-y divide-line">
          {results.data.map((master) => (
            <MasterRow
              key={master.id}
              master={master}
              alreadyMine={alreadyMine.get(master.id)}
              addedName={added[master.id]}
              onAdded={(name) =>
                setAdded((current) => ({ ...current, [master.id]: name }))
              }
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          title={`Nothing in the shared list matches “${debounced}”`}
          description="Import a file instead, or add the product by hand."
        />
      )}
    </Card>
  );
}

function MasterRow({
  master,
  alreadyMine,
  addedName,
  onAdded,
}: {
  master: MasterProduct;
  alreadyMine: AdoptedMaster | undefined;
  addedName: string | undefined;
  onAdded: (name: string) => void;
}) {
  const [price, setPrice] = useState(master.indicative_price);
  const copy = useCopyFromMaster();

  // Still shown, still searchable, but not offered again — a shopkeeper looking
  // for "coke" needs to see that they already have it, which hiding the row
  // would not tell them. The name is the shop's own, so somebody who renamed
  // their copy still recognises it.
  if (alreadyMine) {
    return (
      <li className="flex flex-wrap items-center gap-3 py-3 opacity-60">
        <ProductTile name={master.display_name} src={master.image_thumb_url} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-ink-900">{master.display_name}</p>
          <p className="text-xs text-ink-500">
            Already in your catalogue as{" "}
            <span className="font-medium">{alreadyMine.name}</span>
          </p>
        </div>
        <Button disabled icon={<Check className="size-4" aria-hidden />}>
          Added
        </Button>
      </li>
    );
  }

  if (addedName) {
    return (
      <li className="flex items-center gap-3 py-3 text-sm text-ink-500">
        <Check className="size-4 text-brand-600" aria-hidden />
        <span>
          <span className="font-medium text-ink-900">{addedName}</span> is now
          in your catalogue.
        </span>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-end gap-3 py-3">
      <ProductTile
        name={master.display_name}
        src={master.image_thumb_url}
        className="self-center"
      />

      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink-900">{master.display_name}</p>
        <p className="text-xs text-ink-500">
          {[
            master.category_name,
            master.is_zero_rated ? "Zero rated" : null,
            // The credit travels with the picture. A CC-BY-SA photograph has to
            // be attributed wherever it is shown, and this is where a merchant
            // sees the catalogue's images before adopting one.
            master.image_licence ? `Photo: ${master.image_attribution}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      <TextField
        label="Your price"
        inputMode="decimal"
        value={price}
        onChange={(event) => setPrice(event.target.value)}
        className="w-32"
      />

      <Button
        variant="primary"
        icon={<Plus className="size-4" aria-hidden />}
        disabled={copy.isPending}
        onClick={() =>
          copy.mutate(
            { master_id: master.id, selling_price: price },
            { onSuccess: (product) => onAdded(product.name) },
          )
        }
      >
        Add
      </Button>

      {copy.isError && (
        <p className="w-full text-xs text-danger-700">
          {copy.error instanceof ApiError
            ? copy.error.detail
            : "That could not be added."}
        </p>
      )}
    </li>
  );
}
